import { request } from "$/lib/api"
import {
  CreateServiceEventRequest,
  EventParticipant,
  JobSummary,
  Route,
  SaveServiceEventNoteRequest,
  ServiceEvent,
  UpdateServiceEventRequest,
  populateRoute,
} from "@repo/common"
import {
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"

interface ServiceEventFilters {
  storeId: string
}

async function fetchServiceEvents(storeId: string): Promise<ServiceEvent[]> {
  return await request<ServiceEvent[]>({
    url: populateRoute(Route.SERVICE_EVENT__FIND_ALL_BY_STORE, storeId),
    method: "GET",
  })
}

async function fetchServiceEvent(eventId: string): Promise<ServiceEvent> {
  return await request<ServiceEvent>({
    url: populateRoute(Route.SERVICE_EVENT__FIND_ONE, eventId),
    method: "GET",
  })
}

async function fetchServiceEventJobs(eventId: string): Promise<JobSummary[]> {
  return await request<JobSummary[]>({
    url: populateRoute(Route.SERVICE_EVENT__FIND_JOBS, eventId),
    method: "GET",
  })
}

async function fetchServiceEventParticipants(
  eventId: string,
): Promise<EventParticipant[]> {
  return await request<EventParticipant[]>({
    url: populateRoute(Route.SERVICE_EVENT__FIND_PARTICIPANTS, eventId),
    method: "GET",
  })
}

async function createServiceEvent(
  data: CreateServiceEventRequest,
  storeId: string,
): Promise<ServiceEvent> {
  return await request<ServiceEvent>({
    url: populateRoute(Route.SERVICE_EVENT__CREATE, storeId),
    method: "POST",
    data,
  })
}

async function updateServiceEvent(
  data: UpdateServiceEventRequest,
  eventId: string,
): Promise<ServiceEvent> {
  return await request<ServiceEvent>({
    url: populateRoute(Route.SERVICE_EVENT__UPDATE, eventId),
    method: "PATCH",
    data,
  })
}

async function deleteServiceEvent(eventId: string): Promise<void> {
  return await request<void>({
    url: populateRoute(Route.SERVICE_EVENT__DELETE, eventId),
    method: "DELETE",
  })
}

async function publishServiceEvent(eventId: string): Promise<ServiceEvent> {
  return await request<ServiceEvent>({
    url: populateRoute(Route.SERVICE_EVENT__PUBLISH, eventId),
    method: "POST",
  })
}

async function saveServiceEventNote(
  eventId: string,
  data: SaveServiceEventNoteRequest,
): Promise<void> {
  return await request<void>({
    url: populateRoute(Route.SERVICE_EVENT__SAVE_NOTE, eventId),
    method: "PUT",
    data,
  })
}

async function duplicateServiceEvent(eventId: string): Promise<ServiceEvent> {
  return await request<ServiceEvent>({
    url: populateRoute(Route.SERVICE_EVENT__DUPLICATE, eventId),
    method: "POST",
  })
}

async function uploadServiceEventImage(
  eventId: string,
  image: File,
): Promise<{ imageUrl: string }> {
  const formData = new FormData()
  formData.append("image", image)

  return await request<{ imageUrl: string }>({
    url: populateRoute(Route.SERVICE_EVENT__UPLOAD_IMAGE, eventId),
    method: "POST",
    data: formData,
    headers: {
      "Content-Type": "multipart/form-data",
    },
  })
}

export function useServiceEventMutations(storeId: string) {
  const queryClient = useQueryClient()

  const invalidateQueries = async () => {
    await queryClient.invalidateQueries(serviceEventQueries.list({ storeId }))
  }

  const createMutation = useMutation({
    mutationFn: (data: CreateServiceEventRequest) =>
      createServiceEvent(data, storeId),
    onSettled: invalidateQueries,
  })

  const updateMutation = useMutation({
    mutationFn: ({
      data,
      eventId,
    }: {
      data: UpdateServiceEventRequest
      eventId: string
    }) => updateServiceEvent(data, eventId),
    onSettled: invalidateQueries,
  })

  const deleteMutation = useMutation({
    mutationFn: (eventId: string) => deleteServiceEvent(eventId),
    onSettled: invalidateQueries,
  })

  const publishMutation = useMutation({
    mutationFn: (eventId: string) => publishServiceEvent(eventId),
    onSettled: invalidateQueries,
  })

  const saveNoteMutation = useMutation({
    mutationFn: ({
      eventId,
      data,
    }: {
      eventId: string
      data: SaveServiceEventNoteRequest
    }) => saveServiceEventNote(eventId, data),
  })

  const duplicateMutation = useMutation({
    mutationFn: (eventId: string) => duplicateServiceEvent(eventId),
    onSettled: invalidateQueries,
  })

  const uploadImageMutation = useMutation({
    mutationFn: ({ eventId, image }: { eventId: string; image: File }) =>
      uploadServiceEventImage(eventId, image),
    onSettled: invalidateQueries,
  })

  return {
    createMutation,
    updateMutation,
    deleteMutation,
    publishMutation,
    saveNoteMutation,
    duplicateMutation,
    uploadImageMutation,
  }
}

const serviceEventQueries = {
  all: () => ["service-event"],
  lists: () => [...serviceEventQueries.all(), "list"],
  list: (filters: ServiceEventFilters) =>
    queryOptions({
      queryKey: [...serviceEventQueries.lists(), filters],
      queryFn: () => fetchServiceEvents(filters.storeId),
    }),
  detail: (eventId: string) =>
    queryOptions({
      queryKey: [...serviceEventQueries.all(), "detail", eventId],
      queryFn: () => fetchServiceEvent(eventId),
    }),
  jobs: (eventId: string) =>
    queryOptions({
      queryKey: [...serviceEventQueries.all(), "jobs", eventId],
      queryFn: () => fetchServiceEventJobs(eventId),
    }),
  participants: (eventId: string) =>
    queryOptions({
      queryKey: [...serviceEventQueries.all(), "participants", eventId],
      queryFn: () => fetchServiceEventParticipants(eventId),
    }),
}

export default serviceEventQueries
